import { useEffect, useState } from "react";
import DataTable from "@components/DataTable";
import {
  faPlus,
  faCheck,
  faX,
  faPencil,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form } from "react-bootstrap";
import { TaskRate } from "../types";
import ConfirmationModal from "@components/Modals/ConfirmationModal";
import { useListTaskRates } from "../api";
import {
  useCreateTaskRateForProductMutation,
  useUpdateTaskRateForProductMutation,
  useDeleteTaskRateFromProductMutation,
  V1TaskRateResource,
} from "@generated";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import UnsavedChangesModal from "@components/UnsavedChangesModal";
import EditableDataTable from "@components/EditableDataTable";

const VariableRates = ({ productId, refetchProduct }) => {
  const { accountSlug } = useSelector((state: RootState) => state.slug);
  const { taskRates, taskRatesLoading, taskRatesFetching, refetchTaskRates } =
    useListTaskRates(productId);

  const [createTaskRate] = useCreateTaskRateForProductMutation();
  const [updateTaskRate] = useUpdateTaskRateForProductMutation();
  const [deleteTaskRate] = useDeleteTaskRateFromProductMutation();

  const [successMessage, setSuccessMessage] = useState<string>("");
  const [showSuccessAlert, setShowSuccessAlert] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showFailAlert, setShowFailAlert] = useState<boolean>(false);

  const [initialFixedPrices, setInitialFixedPrices] = useState<TaskRate[]>([]);
  const [fixedPrices, setFixedPrices] = useState<TaskRate[]>([]);

  const [initialFixedCosts, setInitialFixedCosts] = useState<TaskRate[]>([]);
  const [fixedCosts, setFixedCosts] = useState<TaskRate[]>([]);
  const [itemToDelete, setItemToDelete] =
    useState<{ type: string; id: string } | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] =
    useState<boolean>(false);

  const unsavedItem: boolean = [...fixedPrices, ...fixedCosts].some(
    (i) => i.editing && i.isChanged
  );

  useEffect(() => {
    if (productId) {
      refetchTaskRates();
    }
  }, [productId]);

  useEffect(() => {
    const prices = taskRates.filter((rate) => rate.type === "hardware_price");
    const costs = taskRates.filter((rate) => rate.type === "hardware_cost");

    if (!taskRatesLoading && !taskRatesFetching) {
      setInitialFixedPrices(prices);
      setFixedPrices(prices);
      setInitialFixedCosts(costs);
      setFixedCosts(costs);
    }
  }, [taskRatesLoading, taskRatesFetching]);

  const getStateItems = (type) => {
    if (type === "hardware_price") {
      return {
        state: fixedPrices,
        setState: setFixedPrices,
        initial: initialFixedPrices,
        setInitial: setInitialFixedPrices,
      };
    }
    if (type === "hardware_cost") {
      return {
        state: fixedCosts,
        setState: setFixedCosts,
        initial: initialFixedCosts,
        setInitial: setInitialFixedCosts,
      };
    }
    throw new Error(`Unsupported type passed to getStateItems: ${type}`);
  };

  const updateItem = (item) => {
    const { state, setState } = getStateItems(item.type);
    setState(
      state.map((i) => {
        return i.id === item.id ? item : i;
      })
    );
  };

  const cancelItem = (item) => {
    const { state, setState, initial } = getStateItems(item.type);

    const original = initial.find((i) => i.id === item.id);

    if (!original) {
      setState(state.filter((i) => i.id !== item.id));
    } else {
      setState(
        state.map((i) =>
          i.id === item.id
            ? { ...original, editing: false, isChanged: false }
            : i
        )
      );
    }
  };

  const confirmItem = async (item) => {
    let data: V1TaskRateResource = {
      type: "task-rates",
      attributes: {
        "minimum-quantity": item.minQty,
        "base-amount": item.fixedAmt,
        "unit-amount": item.unitAmt,
      },
    };

    if (item.type !== "variable_hour")
      data.attributes!["rate-type"] = item.type;

    try {
      const arg = {
        slug: accountSlug,
        productId,
        body: { data },
      };

      const create = createTaskRate;
      const update = updateTaskRate;

      const res = item.id.includes("new")
        ? // @ts-ignore
          await create(arg)
        : // @ts-ignore
          await update({
            ...arg,
            id: Number(item.id),
            body: { data: { ...arg.body.data, id: Number(item.id) } },
          });

      //@ts-ignore
      const id = res?.data?.data?.id;
      if (id) {
        setSuccessMessage(
          `${
            item.type === "hardware_price"
              ? "Variable Product Price"
              : "Variable Product Cost"
          } ${item.id.includes("new") ? "created" : "updated"}`
        );
        setShowSuccessAlert(true);
        refetchProduct();
        refetchTaskRates();
      } else {
        const message =
          //@ts-ignore
          res?.error?.data?.errors?.[0]?.detail ||
          "Something when wrong saving this item";
        setErrorMessage(message);
        setShowFailAlert(true);
        return;
      }
    } catch {
      setErrorMessage("Something went wrong");
      setShowFailAlert(true);
    }
  };

  const deleteItem = async () => {
    if (itemToDelete?.id) {
      const type = itemToDelete.type;
      const deleteFromAPI = deleteTaskRate;

      //@ts-ignore
      await deleteFromAPI({
        slug: accountSlug,
        productId,
        id: Number(itemToDelete.id),
      });

      setSuccessMessage(
        `${
          type === "hardware_price"
            ? "Variable Product Price"
            : "Variable Product Cost"
        } deleted`
      );
      setShowSuccessAlert(true);
      setShowConfirmDelete(false);
      refetchTaskRates();
    }
  };

  const tableColumns = [
    {
      header() {
        return "Min Qty";
      },
      cell(item) {
        return item.editing ? (
          <Form.Control
            type="number"
            step={1}
            defaultValue={item.minQty}
            onChange={(e) =>
              updateItem({
                ...item,
                minQty: e.target.value,
                isChanged: true,
              })
            }
          />
        ) : (
          <div className="edit">
            <FontAwesomeIcon
              icon={faPencil}
              onClick={() => {
                if (unsavedItem) {
                  setShowUnsavedChangesModal(true);
                  return;
                }
                const { state, setState } = getStateItems(item.type);
                setState(
                  state.map((i) => {
                    return i.id === item.id
                      ? { ...i, editing: true }
                      : { ...i, editing: false };
                  })
                );
              }}
            />
            {item.minQty}
          </div>
        );
      },
    },
    {
      header() {
        return "Fixed Amt";
      },
      cell(item) {
        return item.editing ? (
          <Form.Control
            type="number"
            step={0.01}
            defaultValue={item.fixedAmt}
            onChange={(e) =>
              updateItem({
                ...item,
                fixedAmt: e.target.value,
                isChanged: true,
              })
            }
          />
        ) : (
          item.fixedAmt
        );
      },
    },
    {
      header() {
        return "Unit Amt";
      },
      cell(item) {
        return item.editing ? (
          <>
            <Form.Control
              type="number"
              step={0.01}
              defaultValue={item.unitAmt}
              onChange={(e) =>
                updateItem({
                  ...item,
                  unitAmt: e.target.value,
                  isChanged: true,
                })
              }
            />
            <div className="saveCancel">
              <FontAwesomeIcon
                icon={faCheck}
                onClick={() => confirmItem(item)}
              />
              <FontAwesomeIcon onClick={() => cancelItem(item)} icon={faX} />
            </div>
          </>
        ) : (
          <>
            {item.unitAmt}
            <div className="actionsCell">
              <FontAwesomeIcon
                icon={faTrashCan}
                className="deleteIcon"
                onClick={() => {
                  if (unsavedItem) {
                    setShowUnsavedChangesModal(true);
                    return;
                  }
                  setItemToDelete({
                    type: item.type,
                    id: item.id,
                  });
                  setShowConfirmDelete(true);
                }}
              />
            </div>
          </>
        );
      },
    },
  ];

  return (
    <>
      {handleAlert(
        showFailAlert,
        errorMessage,
        setShowFailAlert,
        "warning",
        ToastAlert
      )}
      {handleAlert(
        showSuccessAlert,
        successMessage,
        setShowSuccessAlert,
        "success",
        ToastAlert
      )}
      <div className="variablePriceCostTable">
        <section>
          <p className="tableTitle">Variable Product Price</p>
          <div className="materialPriceTable">
            {fixedPrices.length ? (
              <EditableDataTable
                records={fixedPrices}
                columns={tableColumns}
                config={{ striped: true, hover: true, bordered: true }}
              />
            ) : (
              <></>
            )}
          </div>
          <div
            className="add"
            onClick={() => {
              if (unsavedItem) {
                setShowUnsavedChangesModal(true);
                return;
              }
              const id = `new-${Date.now()}`;
              setFixedPrices([
                ...fixedPrices.map((i) => {
                  return { ...i, editing: false };
                }),
                {
                  id,
                  type: "hardware_price",
                  minQty: 1,
                  fixedAmt: 0,
                  unitAmt: 0,
                  editing: true,
                  isChanged: true,
                },
              ]);
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="addText">Add Variable Product Price</span>
          </div>
        </section>
        <section>
          <p className="tableTitle">Variable Product Cost</p>
          <div className="materialCostTable">
            {fixedCosts.length ? (
              <EditableDataTable
                records={fixedCosts}
                columns={tableColumns}
                config={{ striped: true, hover: true, bordered: true }}
              />
            ) : (
              <></>
            )}
          </div>
          <div
            className="add"
            onClick={() => {
              if (unsavedItem) {
                setShowUnsavedChangesModal(true);
                return;
              }
              const id = `new-${Date.now()}`;
              setFixedCosts([
                ...fixedCosts.map((i) => {
                  return { ...i, editing: false };
                }),
                {
                  id,
                  type: "hardware_cost",
                  minQty: 1,
                  fixedAmt: 0,
                  unitAmt: 0,
                  editing: true,
                  isChanged: true,
                },
              ]);
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="addText">Add Variable Product Cost</span>
          </div>
        </section>
        <ConfirmationModal
          show={showConfirmDelete}
          title={`Delete ${
            itemToDelete?.type === "hardware_price"
              ? "Variable Product Price"
              : "Variable Product Cost"
          }`}
          message={"Are you sure you want to delete this item?"}
          onConfirm={deleteItem}
          onCancel={() => setShowConfirmDelete(false)}
        />
        <UnsavedChangesModal
          show={showUnsavedChangesModal}
          handleCancel={() => setShowUnsavedChangesModal(false)}
          handleDontSave={() => {
            cancelItem(
              [...fixedPrices, ...fixedCosts].find((i) => i.isChanged)
            );
            setShowUnsavedChangesModal(false);
          }}
          handleSave={() => {
            confirmItem(
              [...fixedPrices, ...fixedCosts].find((i) => i.isChanged)
            );
            setShowUnsavedChangesModal(false);
          }}
        />
      </div>
    </>
  );
};

export default VariableRates;
